import React, { useRef, useEffect, useState } from 'react';
import * as THREE from 'three';
import { OrbitControls, STLLoader } from 'three-stdlib';
import { Loader2 } from 'lucide-react';
import { gsap } from 'gsap';

export const STLViewer = ({ fileUrl, metalColor = '#E0E0E5', onThumbnailReady }) => {
  const containerRef = useRef(null);
  const [loading, setLoading] = useState(true);

  // Keep mesh in ref so it is accessible outside useEffect
  const meshRef = useRef(null);
  const pivotRef = useRef(null);

  useEffect(() => {
    if (!containerRef.current) return;

    let scene, camera, renderer, controls, animationId;

    const width = containerRef.current.clientWidth;
    const height = containerRef.current.clientHeight;

    // Scene
    scene = new THREE.Scene();
    scene.background = new THREE.Color('#ffffff');

    // Renderer
    renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(width, height);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.physicallyCorrectLights = true;
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.2;
    containerRef.current.appendChild(renderer.domElement);

    // Camera
    camera = new THREE.PerspectiveCamera(45, width / height, 0.1, 1000);
    camera.position.set(5, 2.5, 5);

    // Controls
    controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.05;
    controls.screenSpacePanning = false;
    controls.minDistance = 3;  // zoom in limit
    controls.maxDistance = 10; // zoom out limit
    controls.maxPolarAngle = Math.PI / 1.8; // prevent flipping under ring

    // Lights
    const ambient = new THREE.AmbientLight(0xffffff, 2.5);
    scene.add(ambient);

    const mainLight = new THREE.DirectionalLight(0xffffff, 3.0);
    mainLight.position.set(8, 10, 5);
    mainLight.castShadow = true;
    mainLight.shadow.mapSize.width = 2048;
    mainLight.shadow.mapSize.height = 2048;
    scene.add(mainLight);

    const fillLight = new THREE.DirectionalLight(0xffffff, 1.5);
    fillLight.position.set(-8, 5, -5);
    scene.add(fillLight);

    const backLight = new THREE.DirectionalLight(0xffffff, 2.0);
    backLight.position.set(0, 8, -10);
    scene.add(backLight);

    const rimLight = new THREE.DirectionalLight(0xffffff, 1.0);
    rimLight.position.set(5, 2, -8);
    scene.add(rimLight);

    // Ground
    const groundGeometry = new THREE.PlaneGeometry(30, 30);
    const groundMaterial = new THREE.ShadowMaterial({ opacity: 0.08 });
    const ground = new THREE.Mesh(groundGeometry, groundMaterial);
    ground.rotation.x = -Math.PI / 2;
    ground.position.y = 0;
    ground.receiveShadow = true;
    scene.add(ground);

    // Grid
    const grid = new THREE.GridHelper(20, 40, 0xdddddd, 0xf0f0f0);
    grid.position.y = 0.01;
    scene.add(grid);

    // Environment map
    const pmremGenerator = new THREE.PMREMGenerator(renderer);
    pmremGenerator.compileEquirectangularShader();
    new THREE.TextureLoader().load(
      'https://threejs.org/examples/textures/2294472375_24a3b8ef46_o.jpg',
      (texture) => {
        texture.mapping = THREE.EquirectangularReflectionMapping;
        const envMap = pmremGenerator.fromEquirectangular(texture).texture;
        scene.environment = envMap;
        texture.dispose();
        pmremGenerator.dispose();
      }
    );

    // Pivot group
    const pivot = new THREE.Group();
    scene.add(pivot);
    pivotRef.current = pivot;

    // Animate loop
    const animate = () => {
      animationId = requestAnimationFrame(animate);
      if (pivotRef.current) {
        pivotRef.current.rotation.y += 0.006; // smooth horizontal rotation
      }
      controls.update();
      renderer.render(scene, camera);
    };
    animate();

    // Load STL
    const loader = new STLLoader();
    loader.load(
      fileUrl,
      (geometry) => {
        geometry.center(); // center geometry in local space

        const bbox = geometry.boundingBox || new THREE.Box3().setFromObject(meshRef.current);
        const size = new THREE.Vector3();
        bbox.getSize(size);
        const scale = 4.5 / Math.max(size.x, size.y, size.z);
        geometry.scale(scale, scale, scale);

        // Material
        const material = new THREE.MeshStandardMaterial({
          color: new THREE.Color(metalColor),
          metalness: 1,
          roughness: 0.15,
        });

        // Mesh
        const mesh = new THREE.Mesh(geometry, material);
        mesh.castShadow = true;
        mesh.receiveShadow = true;

        // Rotate band flat
        mesh.rotation.x = -Math.PI / 2;

        // Set mesh at center of pivot
        mesh.position.set(0, size.z * scale / 2 + 0.1, 0);

        pivot.add(mesh);
        meshRef.current = mesh;

        // Controls target
        controls.target.set(0, mesh.position.y, 0);
        controls.update();

        setLoading(false);

        // Thumbnail
        setTimeout(() => {
          if (onThumbnailReady) {
            renderer.render(scene, camera);
            const thumbnail = renderer.domElement.toDataURL('image/png');
            onThumbnailReady(thumbnail);
          }
        }, 500);
      },
      undefined,
      (err) => {
        console.error('STL load error:', err);
        setLoading(false);
      }
    );

    // Resize
    const handleResize = () => {
      if (!containerRef.current) return;
      const w = containerRef.current.clientWidth;
      const h = containerRef.current.clientHeight;
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h);
    };
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (animationId) cancelAnimationFrame(animationId);
      if (controls) controls.dispose();
      if (meshRef.current) {
        pivot.remove(meshRef.current);
        meshRef.current.geometry.dispose();
        meshRef.current.material.dispose();
      }
      if (renderer && containerRef.current) {
        containerRef.current.removeChild(renderer.domElement);
        renderer.dispose();
      }
    };
  }, [fileUrl]);

  // Smooth color transition when metalColor changes
  useEffect(() => {
    if (!meshRef.current) return;
    const mesh = meshRef.current;
    gsap.to(mesh.material.color, {
      r: new THREE.Color(metalColor).r,
      g: new THREE.Color(metalColor).g,
      b: new THREE.Color(metalColor).b,
      duration: 0.5,
    });
  }, [metalColor]);

  return (
    <div className="w-full h-full relative" style={{ minHeight: '500px' }}>
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-900 rounded-md z-10">
          <div className="text-center">
            <Loader2 className="w-12 h-12 animate-spin text-yellow-500 mx-auto mb-4" />
            <p className="text-sm text-white">Loading 3D model...</p>
          </div>
        </div>
      )}
      <div
        ref={containerRef}
        className="w-full h-full rounded-md"
        data-testid="stl-viewer"
        style={{ minHeight: '500px', touchAction: 'none', cursor: 'grab' }}
      />
      <div className="absolute bottom-4 left-4 text-xs text-white bg-black/70 px-3 py-2 rounded pointer-events-none">
        <div>🖱️ Left click + drag = Rotate</div>
        <div>🖱️ Right click + drag = Pan</div>
        <div>⚪ Mouse wheel = Zoom</div>
      </div>
    </div>
  );
};